// ========================================================================
// FILE: src/pages/Admin/DaftarPelangganAdmin.jsx
// PENJELASAN: Halaman untuk admin melihat semua pelanggan per mitra
// ========================================================================
import React, { useState, useEffect } from 'react';
import { getAllCustomers } from '../../supabase/database';
import { FaUsers, FaPhone, FaMapMarkerAlt, FaSearch, FaFilter } from 'react-icons/fa';
import Spinner from '../../components/ui/Spinner';

const DaftarPelangganAdmin = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMitra, setSelectedMitra] = useState('');
  const [mitraList, setMitraList] = useState([]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm, selectedMitra]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const customerList = await getAllCustomers();
      setCustomers(customerList);
      
      // Extract unique mitra for filter
      const uniqueMitra = [...new Set(customerList.map(customer => customer.nama_mitra))];
      setMitraList(uniqueMitra);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError('Gagal mengambil data pelanggan');
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = customers;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.nama_pelanggan.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.no_wa.includes(searchTerm) ||
        customer.alamat.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.nama_mitra.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by mitra
    if (selectedMitra) {
      filtered = filtered.filter(customer => customer.nama_mitra === selectedMitra);
    }

    setFilteredCustomers(filtered);
  };

  const getCustomersByMitra = () => {
    const grouped = {};
    filteredCustomers.forEach(customer => {
      if (!grouped[customer.nama_mitra]) {
        grouped[customer.nama_mitra] = [];
      }
      grouped[customer.nama_mitra].push(customer);
    });
    return grouped;
  };

  if (loading) return <Spinner />;

  const groupedCustomers = getCustomersByMitra();

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <FaUsers className="text-4xl text-purple-500" />
        <h1 className="text-3xl font-bold text-gray-800">Daftar Pelanggan (Admin)</h1>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Cari pelanggan, mitra, atau alamat..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
          <div className="relative">
            <FaFilter className="absolute left-3 top-3 text-gray-400" />
            <select
              value={selectedMitra}
              onChange={(e) => setSelectedMitra(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Semua Mitra</option>
              {mitraList.map(mitra => (
                <option key={mitra} value={mitra}>{mitra}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-100 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-blue-800">Total Pelanggan</h3>
          <p className="text-2xl font-bold text-blue-600">{filteredCustomers.length}</p>
        </div>
        <div className="bg-green-100 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-green-800">Total Mitra</h3>
          <p className="text-2xl font-bold text-green-600">{Object.keys(groupedCustomers).length}</p>
        </div>
        <div className="bg-yellow-100 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-yellow-800">Rata-rata per Mitra</h3>
          <p className="text-2xl font-bold text-yellow-600">
            {Object.keys(groupedCustomers).length > 0 
              ? Math.round(filteredCustomers.length / Object.keys(groupedCustomers).length)
              : 0}
          </p>
        </div>
      </div>

      {/* Customer List by Mitra */}
      {Object.keys(groupedCustomers).length > 0 ? (
        Object.entries(groupedCustomers).map(([mitraName, customers]) => (
          <div key={mitraName} className="mb-8">
            <div className="bg-purple-100 p-4 rounded-t-lg border-b">
              <h2 className="text-xl font-bold text-purple-800">
                {mitraName} ({customers.length} pelanggan)
              </h2>
            </div>
            
            <div className="bg-white rounded-b-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nama Pelanggan
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        No WhatsApp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Alamat
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tanggal Ditambahkan
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FaUsers className="text-gray-400 mr-3" />
                            <div className="text-sm font-medium text-gray-900">
                              {customer.nama_pelanggan}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FaPhone className="text-gray-400 mr-3" />
                            <div className="text-sm text-gray-900">{customer.no_wa}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-start">
                            <FaMapMarkerAlt className="text-gray-400 mr-3 mt-1" />
                            <div className="text-sm text-gray-900 max-w-xs">
                              {customer.alamat}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {customer.createdAt?.toLocaleDateString ? customer.createdAt.toLocaleDateString('id-ID') : 
                           customer.createdAt ? new Date(customer.createdAt).toLocaleDateString('id-ID') : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-12">
          <FaUsers className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500">
            {searchTerm || selectedMitra ? 'Tidak ada pelanggan yang sesuai dengan filter' : 'Belum ada data pelanggan'}
          </p>
        </div>
      )}
    </div>
  );
};

export default DaftarPelangganAdmin;
